import { makeAutoObservable } from 'mobx';
import { component } from 'tsdi';
import * as React from 'react';
import { injectTSDI } from '../tsdi';
import { featureEnabled } from '../features';
import { CookieNoticeStore } from '../../components/cookie-notice/store';

const facebookPixelEvents = [
    'PageView',
    'AddToCard',
    'trials-training',
    'membership',
    'InitiateCheckout'
] as const;
type FacebookPixelEventType = typeof facebookPixelEvents[number];

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const _fbq = (...params: any[]) => (window as any).fbq?.(...params);

@component
export class FacebookPixel {
    constructor() {
        makeAutoObservable(this);
    }

    private cookieNoticeStore = injectTSDI(CookieNoticeStore);

    private initialized = false;

    private initializedPixels: string[] = [];

    private disabledPixels: string[] = [];

    private get enabledPixels() {
        return this.initializedPixels.filter(
            (pixel) => !this.disabledPixels.includes(pixel)
        );
    }

    private initialize = () => {
        /* eslint-disable */
        // @ts-ignore
        // prettier-ignore
        !function(f,b,e,v,n,t,s) {if(f.fbq)return;n=f.fbq=function(){n.callMethod? n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window, document,'script', 'https://connect.facebook.net/en_US/fbevents.js');
        /* eslint-enable */

        this.initialized = true;
    };

    public enablePixel = (pixelId: string) => {
        if (!this.initialized) {
            this.initialize();
        }

        if (this.disabledPixels.includes(pixelId)) {
            this.reEnablePixel(pixelId);
            return;
        }

        if (this.initializedPixels.includes(pixelId)) {
            return;
        }

        this.initializedPixels.push(pixelId);
        _fbq('init', pixelId);
        this.trackEvent('PageView');
    };

    public disablePixel = (pixelId: string) => {
        this.disabledPixels.push(pixelId);
        _fbq('set', 'autoConfig', false, pixelId);
    };

    private reEnablePixel = (pixelId: string) => {
        this.disabledPixels = this.disabledPixels.filter(
            (pixel) => pixel === pixelId
        );
        _fbq('set', 'autoConfig', true, pixelId);
    };

    public trackEvent = (
        event: FacebookPixelEventType | string,
        params?: unknown
    ) => {
        if (!this.cookieNoticeStore.marketingEventsEnabled) {
            return;
        }
        this.enabledPixels.forEach((pixelId) => {
            const isCustomEvent = !facebookPixelEvents.includes(
                event as FacebookPixelEventType
            );
            _fbq(
                isCustomEvent ? 'trackSingleCustom' : 'trackSingle',
                pixelId,
                event,
                params
            );
            if (featureEnabled('IS_DEV')) {
                console.info(`fbPixel: tracked event ${event}`);
            }
        });
    };
}

export const useScopedFacebookPixel = (pixelId?: string) => {
    const facebookPixelStore = injectTSDI(FacebookPixel);

    React.useEffect(() => {
        if (pixelId) {
            facebookPixelStore.enablePixel(pixelId);
        }

        return () => {
            if (pixelId) {
                facebookPixelStore.disablePixel(pixelId);
            }
        };
    }, [pixelId]);
};
